(() => {
  const BUNDLE_ROOT = '#simple-bundles-io-options';
  const SUMMARY_SOURCE = '[data-elita-checkout-summary]';
  const MIRROR_ATTR = 'data-elita-cart-summary-mirror';

  const OPUS_HOST = '#opus-shadow-container';
  const ITEM = '.cd-product-item-content';
  const TITLE = '.cd-product-item-title';
  const OPTION = '.cd-product-item-options';
  const OPTION_KEY = '.title-option';

  const STYLE_ID = 'elita-bundle-cart-style';
  const SUMMARY = 'elita-bundle-cart-summary';
  const HIDDEN = 'elita-bundle-cart-hidden';

  let opusRoot = null;
  let opusObserver = null;
  let bootObserver = null;
  let frame = 0;
  let lastSignature = '';
  let cachedCart = null;
  let requestToken = 0;


  /* ---------------------------------------------------------
     PDP → Shopify private property
     --------------------------------------------------------- */

  function privateSummaryName(name) {
    const match = String(name || '').match(/^properties\[(.+)\]$/);

    if (!match) return name;

    const key = match[1];

    return `properties[${key.startsWith('_') ? key : `_${key}`}]`;
  }

  function getBundleSource() {
    const root = document.querySelector(BUNDLE_ROOT);
    const source = root?.querySelector(SUMMARY_SOURCE);

    return root && source
      ? { root, source }
      : null;
  }

  function findAddForm(root) {
    const forms = [
      ...document.querySelectorAll('form[action*="/cart/add"]')
    ].filter(
      form => form.querySelector('[name="add"]')
    );

    if (!forms.length) return null;

    const variantId = String(
      root?.dataset?.simpleBundlesVariantId || ''
    );

    if (variantId) {
      const exact = forms.find(
        form =>
          String(
            form.querySelector('[name="id"]')?.value || ''
          ) === variantId
      );

      if (exact) return exact;
    }

    return forms[0];
  }

  function syncSummaryIntoForm(form = null) {
    const bundle = getBundleSource();

    if (!bundle) return;

    const target =
      form ||
      findAddForm(bundle.root);

    const expected =
      findAddForm(bundle.root);

    if (
      !target ||
      (expected && target !== expected)
    ) {
      return;
    }

    let mirror =
      target.querySelector(
        `[${MIRROR_ATTR}]`
      );

    const value =
      String(
        bundle.source.value || ''
      ).trim();

    if (
      bundle.source.disabled ||
      !bundle.source.name ||
      !value
    ) {
      mirror?.remove();
      return;
    }

    if (!mirror) {
      mirror =
        document.createElement('input');

      mirror.type = 'hidden';

      mirror.setAttribute(
        MIRROR_ATTR,
        ''
      );

      target.appendChild(mirror);
    }

    mirror.name =
      privateSummaryName(
        bundle.source.name
      );

    mirror.value =
      value;

    mirror.disabled =
      false;
  }


  /* Sync before Dawn/Simple Bundles handles ATC */

  document.addEventListener(
    'click',
    event => {
      const button =
        event.target.closest?.(
          '[name="add"]'
        );

      if (!button) return;

      const form =
        button.closest(
          'form[action*="/cart/add"]'
        );

      if (form) {
        syncSummaryIntoForm(form);
      }

      if (!opusRoot) {
        requestAnimationFrame(
          tryMountOpus
        );
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    event => {
      const form =
        event.target;

      if (
        form instanceof HTMLFormElement &&
        form.matches(
          'form[action*="/cart/add"]'
        )
      ) {
        syncSummaryIntoForm(form);
      }
    },
    true
  );


  /* ---------------------------------------------------------
     Choice normalization
     --------------------------------------------------------- */

  function collapseEntries(entries) {
    const order = [];
    const counts = new Map();

    entries.forEach(
      entry => {
        if (!counts.has(entry)) {
          order.push(entry);
        }

        counts.set(
          entry,
          (counts.get(entry) || 0) + 1
        );
      }
    );

    return order
      .map(
        entry =>
          `${counts.get(entry)}× ${entry}`
      )
      .join(' · ');
  }

  function normalizeChoiceValue(raw) {
    const value =
      String(raw || '').trim();

    if (!value) return '';

    const parts =
      value
        .split(/\s*\/\s*/)
        .map(
          part => part.trim()
        )
        .filter(Boolean);

    if (parts.length < 2) {
      return value;
    }


    /*
     * Example:
     * 1) Hvid 32-35 / 2) Hvid 36-39 / 3) Hvid 36-39
     *
     * →
     *
     * 1× Hvid 32-35 · 2× Hvid 36-39
     */

    const indexed =
      parts.map(
        part =>
          part.match(
            /^\d+\s*[):.]\s*(.+)$/
          )
      );

    if (indexed.every(Boolean)) {
      return collapseEntries(
        indexed.map(
          match =>
            match[1].trim()
        )
      );
    }


    /*
     * Already count-based:
     *
     * 2x Sort / 1× Hvid
     *
     * →
     *
     * 2× Sort · 1× Hvid
     */

    const counted =
      parts.map(
        part =>
          part.match(
            /^(\d+)\s*[xX×]\s*(.+)$/
          )
      );

    if (counted.every(Boolean)) {
      const order = [];
      const counts = new Map();

      counted.forEach(
        match => {
          const item =
            match[2].trim();

          const qty =
            Number(match[1]);

          if (!counts.has(item)) {
            order.push(item);
          }

          counts.set(
            item,
            (counts.get(item) || 0) + qty
          );
        }
      );

      return order
        .map(
          item =>
            `${counts.get(item)}× ${item}`
        )
        .join(' · ');
    }

    return value;
  }

  function parseChoices(raw) {
    return String(raw || '')
      .split(/\s*·\s*/)
      .map(
        part => part.trim()
      )
      .filter(Boolean)
      .map(
        part => {
          const colon =
            part.indexOf(':');

          if (colon === -1) {
            return {
              label: '',
              value:
                normalizeChoiceValue(
                  part
                )
            };
          }

          return {
            label:
              part
                .slice(0, colon)
                .trim(),

            value:
              normalizeChoiceValue(
                part
                  .slice(colon + 1)
                  .trim()
              )
          };
        }
      );
  }


  /* ---------------------------------------------------------
     Shopify cart helpers
     --------------------------------------------------------- */

  function getChoices(cartItem) {
    const properties =
      cartItem?.properties || {};

    return String(
      properties._Valg ||
      properties['_Your choices'] ||
      properties.Valg ||
      properties['Your choices'] ||
      ''
    ).trim();
  }

  function extractVariantId(className) {
    return (
      String(className || '')
        .match(
          /(?:^|\s)id(\d+)(?:\s|$)/
        )?.[1] ||
      ''
    );
  }

  function pickCartItem(
    items,
    variantId,
    title,
    used
  ) {
    let index =
      items.findIndex(
        (item, i) =>
          !used.has(i) &&
          variantId &&
          String(item.variant_id) ===
            String(variantId)
      );

    if (
      index === -1 &&
      title
    ) {
      index =
        items.findIndex(
          (item, i) =>
            !used.has(i) &&
            String(
              item.product_title || ''
            ).trim() === title
        );
    }

    if (index === -1) {
      index =
        items.findIndex(
          (_, i) =>
            !used.has(i)
        );
    }

    if (index === -1) {
      return null;
    }

    used.add(index);

    return items[index];
  }


  /* ---------------------------------------------------------
     Opus Shadow DOM CSS
     --------------------------------------------------------- */

  function injectStyles(root) {
    if (
      root.getElementById(
        STYLE_ID
      )
    ) {
      return;
    }

    const style =
      document.createElement(
        'style'
      );

    style.id = STYLE_ID;

    style.textContent = `
      .${HIDDEN}{
        display:none!important
      }

      .${SUMMARY}{
        display:grid;
        gap:.55rem;
        margin-top:.6rem;
        margin-bottom:.15rem;
        min-width:0
      }

      .${SUMMARY}__row{
        min-width:0
      }

      .${SUMMARY}__label{
        display:block;
        margin:0;
        color:currentColor;
        font-size:1.08rem;
        font-weight:600;
        line-height:1.3
      }

      .${SUMMARY}__value{
        display:block;
        margin-top:.06rem;
        color:currentColor;
        font-size:1.04rem;
        font-weight:400;
        line-height:1.38;
        opacity:.68;
        overflow-wrap:anywhere
      }

      .${SUMMARY}__row--plain
      .${SUMMARY}__value{
        margin-top:0
      }
    `;

    root.appendChild(style);
  }


  /* ---------------------------------------------------------
     Hide raw properties if Opus ever renders them
     --------------------------------------------------------- */

  function hideRawProperties(item) {
    item
      .querySelectorAll(OPTION)
      .forEach(
        row => {
          const key =
            String(
              row.querySelector(
                OPTION_KEY
              )?.textContent ||
              ''
            )
              .trim()
              .replace(
                /:\s*$/,
                ''
              )
              .trim();

          const lower =
            key.toLowerCase();

          row.classList.toggle(
            HIDDEN,

            key.startsWith('_') ||
            lower === 'valg' ||
            lower ===
              'your choices'
          );
        }
      );
  }


  /* ---------------------------------------------------------
     Render summary
     --------------------------------------------------------- */

  function renderSummary(
    item,
    raw
  ) {
    const title =
      item.querySelector(TITLE);

    let summary =
      item.querySelector(
        `.${SUMMARY}`
      );

    if (
      !title ||
      !raw
    ) {
      summary?.remove();
      return;
    }

    const sections =
      parseChoices(raw);

    if (!sections.length) {
      summary?.remove();
      return;
    }

    if (!summary) {
      summary =
        document.createElement(
          'div'
        );

      summary.className =
        SUMMARY;

      title.insertAdjacentElement(
        'afterend',
        summary
      );
    }

    const signature =
      JSON.stringify(sections);

    if (
      summary.dataset.signature ===
      signature
    ) {
      return;
    }

    summary.dataset.signature =
      signature;

    const fragment =
      document.createDocumentFragment();

    sections.forEach(
      section => {
        const row =
          document.createElement(
            'div'
          );

        row.className =
          `${SUMMARY}__row${
            section.label
              ? ''
              : ` ${SUMMARY}__row--plain`
          }`;

        if (section.label) {
          const label =
            document.createElement(
              'span'
            );

          label.className =
            `${SUMMARY}__label`;

          label.textContent =
            section.label;

          row.appendChild(label);
        }

        if (section.value) {
          const value =
            document.createElement(
              'span'
            );

          value.className =
            `${SUMMARY}__value`;

          value.textContent =
            section.value;

          row.appendChild(value);
        }

        fragment.appendChild(row);
      }
    );

    summary.replaceChildren(
      fragment
    );
  }


  /* ---------------------------------------------------------
     Render entire cart
     --------------------------------------------------------- */

  function renderCart(cart) {
    if (!opusRoot) return;

    const domItems = [
      ...opusRoot.querySelectorAll(
        ITEM
      )
    ];

    const cartItems =
      cart?.items || [];

    const used =
      new Set();

    domItems.forEach(
      item => {
        hideRawProperties(item);

        const variantId =
          extractVariantId(
            item.className
          );

        const title =
          item
            .querySelector(TITLE)
            ?.textContent
            ?.trim() ||
          '';

        const cartItem =
          pickCartItem(
            cartItems,
            variantId,
            title,
            used
          );

        renderSummary(
          item,
          getChoices(cartItem)
        );
      }
    );
  }


  /* ---------------------------------------------------------
     Shopify /cart.js
     --------------------------------------------------------- */

  async function fetchCart() {
    const token =
      ++requestToken;

    try {
      const response =
        await fetch(
          '/cart.js',
          {
            credentials:
              'same-origin',

            cache:
              'no-store',

            headers: {
              Accept:
                'application/json'
            }
          }
        );

      if (!response.ok) {
        return;
      }

      const cart =
        await response.json();

      if (
        token !==
        requestToken
      ) {
        return;
      }

      cachedCart = cart;

      renderCart(cart);
    }

    catch (_) {}
  }


  /* ---------------------------------------------------------
     Only refetch when cart item identity changes
     --------------------------------------------------------- */

  function cartSignature() {
    if (!opusRoot) return '';

    return [
      ...opusRoot.querySelectorAll(
        ITEM
      )
    ]
      .map(
        item =>
          `${
            item.className
          }|${
            item
              .querySelector(TITLE)
              ?.textContent
              ?.trim() ||
            ''
          }`
      )
      .join('||');
  }

  function inspectCart() {
    frame = 0;

    const signature =
      cartSignature();

    if (
      signature ===
      lastSignature
    ) {
      if (cachedCart) {
        renderCart(cachedCart);
      }

      return;
    }

    lastSignature =
      signature;

    fetchCart();
  }

  function scheduleInspect() {
    if (!frame) {
      frame =
        requestAnimationFrame(
          inspectCart
        );
    }
  }


  /* ---------------------------------------------------------
     Opus mount
     --------------------------------------------------------- */

  function mountOpus(root) {
    if (
      !root ||
      root === opusRoot
    ) {
      return;
    }

    opusObserver?.disconnect();

    opusRoot = root;
    lastSignature = '';

    injectStyles(root);
    scheduleInspect();

    opusObserver =
      new MutationObserver(
        scheduleInspect
      );

    opusObserver.observe(
      root,
      {
        childList: true,
        subtree: true,
        characterData: true
      }
    );
  }

  function tryMountOpus() {
    const root =
      document.querySelector(
        OPUS_HOST
      )?.shadowRoot;

    if (!root) {
      return false;
    }

    mountOpus(root);

    return true;
  }


  /* ---------------------------------------------------------
     Boot
     --------------------------------------------------------- */

  if (!tryMountOpus()) {
    bootObserver =
      new MutationObserver(
        () => {
          if (!tryMountOpus()) {
            return;
          }

          bootObserver.disconnect();
          bootObserver = null;
        }
      );

    bootObserver.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true
      }
    );

    /*
     * Don't leave boot observer alive forever
     * on a page where Opus never mounts.
     */
    setTimeout(
      () => {
        bootObserver?.disconnect();
        bootObserver = null;
      },
      10000
    );
  }
})();